import { useEffect, useState, useMemo } from "react";
import { api, apiError } from "@/lib/api";
import { PageHeader, Card, Loading, EmptyState, Btn } from "@/components/primitives";
import { fmtDate, monthLabel } from "@/lib/format";
import { toast } from "sonner";
import { FileText, Download, Eye, Loader2, Sparkles } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export default function Documents() {
  const [docs, setDocs] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [preview, setPreview] = useState(null); // { url, mime, title }

  useEffect(() => { api.get("/documents").then((r) => setDocs(r.data)).catch(() => setDocs([])); }, []);

  const fetchBlob = async (id) => {
    setBusyId(id);
    try {
      const { data } = await api.get(`/documents/${id}`);
      const mime = data.mime || "application/octet-stream";
      let b64 = data.data_b64;
      if (!b64) { toast.error("No file data available"); return null; }
      if (b64.includes(",")) b64 = b64.split(",")[1]; // strip any data: prefix
      const bytes = atob(b64);
      const arr = new Uint8Array(bytes.length);
      for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
      const blob = new Blob([arr], { type: mime });
      return { url: URL.createObjectURL(blob), title: data.title, mime };
    } catch (e) { toast.error(apiError(e)); return null; } finally { setBusyId(null); }
  };
  const view = async (doc) => {
    const d = await fetchBlob(doc.id);
    if (!d) return;
    setPreview({ url: d.url, mime: d.mime, title: d.title || doc.title });
  };
  const closePreview = () => {
    setPreview((p) => { if (p) setTimeout(() => URL.revokeObjectURL(p.url), 500); return null; });
  };
  const download = async (doc) => {
    const d = await fetchBlob(doc.id);
    if (!d) return;
    const a = document.createElement("a");
    a.href = d.url;
    a.download = (doc.title || "document").replace(/[^\w.-]+/g, "_") + (d.mime.includes("pdf") ? ".pdf" : "");
    a.click();
    setTimeout(() => URL.revokeObjectURL(d.url), 60000);
  };

  const groups = useMemo(() => {
    if (!docs) return [];
    const map = {};
    [...docs].sort((a, b) => new Date(b.created_at) - new Date(a.created_at)).forEach((d) => {
      const key = d.month || (d.created_at || "").slice(0, 7) || "—";
      (map[key] = map[key] || []).push(d);
    });
    return Object.entries(map).sort((a, b) => b[0].localeCompare(a[0]));
  }, [docs]);

  if (docs === null) return <Loading />;

  return (
    <div className="max-w-4xl">
      <PageHeader title="Documents" subtitle="Monthly paperwork & uploads." />
      {docs.length === 0 ? (
        <Card><EmptyState icon={FileText} title="No documents yet" subtitle="Run sheets and agency forms generated from transports appear here." /></Card>
      ) : (
        <div className="space-y-5">
          {groups.map(([month, items]) => (
            <div key={month} data-testid={`doc-group-${month}`}>
              <h2 className="text-[11px] uppercase tracking-[0.08em] font-semibold text-curtron-muted mb-2">{monthLabel(month).toUpperCase()}</h2>
              <Card className="divide-y divide-curtron-line">
                {items.map((d) => (
                  <div key={d.id} className="flex items-center gap-3 px-4 py-3.5" data-testid={`document-row-${d.id}`}>
                    <span className="w-9 h-9 rounded-md bg-curtron-green/10 text-curtron-green flex items-center justify-center shrink-0"><FileText className="w-4 h-4" /></span>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-curtron-ink text-sm truncate">{d.title}</span>
                        {d.auto_generated && <span className="inline-flex items-center gap-1 text-[10px] text-curtron-moss bg-curtron-moss/15 rounded px-1.5 py-0.5 shrink-0"><Sparkles className="w-3 h-3" />auto</span>}
                      </div>
                      <div className="text-xs text-curtron-muted truncate">{d.uploaded_by_name || "—"} · {fmtDate(d.created_at)}</div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <Btn variant="ghost" onClick={() => view(d)} disabled={busyId === d.id} data-testid={`doc-view-${d.id}`} className="h-8 px-2">
                        {busyId === d.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />}
                      </Btn>
                      <Btn variant="ghost" onClick={() => download(d)} disabled={busyId === d.id} data-testid={`doc-download-${d.id}`} className="h-8 px-2"><Download className="w-4 h-4" /></Btn>
                    </div>
                  </div>
                ))}
              </Card>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!preview} onOpenChange={(v) => !v && closePreview()}>
        <DialogContent className="max-w-4xl w-[95vw]" data-testid="doc-preview-dialog">
          <DialogHeader>
            <DialogTitle className="truncate pr-6">{preview?.title}</DialogTitle>
          </DialogHeader>
          {preview && (
            preview.mime.startsWith("image/") ? (
              <img src={preview.url} alt={preview.title} className="w-full max-h-[75vh] object-contain rounded-md" />
            ) : (
              <iframe src={preview.url} title={preview.title} className="w-full h-[75vh] rounded-md border border-curtron-line" data-testid="doc-preview-frame" />
            )
          )}
          <div className="flex justify-end">
            <a href={preview?.url} download={`${(preview?.title || "document").replace(/[^\w.-]+/g, "_")}.pdf`}>
              <Btn variant="secondary" data-testid="doc-preview-download"><Download className="w-4 h-4" /> Download</Btn>
            </a>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
